// src/pages/portal/Billing.js
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { collection, query, where, orderBy, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";

export default function Billing() {
  const { currentUser } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Billing Profile State
  const [billingInfo, setBillingInfo] = useState({ companyName: "", tin: "", address: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    
    async function loadBillingData() {
      try {
        // 1. Fetch Payment History
        const paymentsSnap = await getDocs(query(collection(db, "payments"), where("uid", "==", currentUser.uid), orderBy("createdAt", "desc")));
        setPayments(paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // 2. Fetch User's Saved Billing Profile
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists() && userDoc.data().billingInfo) {
          setBillingInfo(userDoc.data().billingInfo);
        }
      } catch (err) {
        console.error("Error loading billing data:", err);
      } finally {
        setLoading(false);
      }
    }
    
    loadBillingData();
  }, [currentUser]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await updateDoc(doc(db, "users", currentUser.uid), {
        billingInfo: billingInfo
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving billing profile:", err);
    } finally {
      setSavingProfile(false);
    }
  };

  const statusStyle = { paid: "badge-accent", pending: "badge-amber", failed: "badge-red", refunded: "badge-blue" };

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "var(--font-head)", marginBottom: 4 }}>Billing & Payments</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Manage your billing details, make payments, and view official receipts.</p>
      </motion.div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, marginBottom: 36 }}>
        
        {/* ── 1. BILLING PROFILE FORM ── */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "20px 24px", height: "100%" }}>
            <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 16 }}>Billing Details</h2>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: 20 }}>
              Please provide your details so we can issue your official BIR-compliant receipts.
            </p>
            
            {profileSuccess && (
              <div style={{ padding: "10px", background: "rgba(0,201,167,0.1)", border: "1px solid rgba(0,201,167,0.3)", borderRadius: 8, color: "var(--accent)", fontSize: "0.8125rem", marginBottom: 16 }}>
                ✓ Billing details saved successfully.
              </div>
            )}

            <form onSubmit={handleSaveProfile}>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: "0.8125rem" }}>Company / Registered Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  style={{ padding: "10px 12px", fontSize: "0.875rem" }}
                  placeholder="e.g. Aimble I.T. Solutions" 
                  value={billingInfo.companyName} 
                  onChange={e => setBillingInfo({...billingInfo, companyName: e.target.value})} 
                />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: "0.8125rem" }}>TIN (Tax Identification Number)</label>
                <input 
                  type="text" 
                  className="form-input" 
                  style={{ padding: "10px 12px", fontSize: "0.875rem" }}
                  placeholder="XXX-XXX-XXX-XXX" 
                  value={billingInfo.tin} 
                  onChange={e => setBillingInfo({...billingInfo, tin: e.target.value})} 
                />
              </div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label" style={{ fontSize: "0.8125rem" }}>Full Address</label>
                <textarea 
                  className="form-textarea" 
                  style={{ minHeight: "80px", padding: "10px 12px", fontSize: "0.875rem" }}
                  placeholder="Registered business address..." 
                  value={billingInfo.address} 
                  onChange={e => setBillingInfo({...billingInfo, address: e.target.value})} 
                />
              </div>
              <button type="submit" className="btn btn-secondary btn-sm" disabled={savingProfile}>
                {savingProfile ? "Saving..." : "Save Billing Details"}
              </button>
            </form>
          </div>
        </motion.div>

        {/* ── 2. MAKE A PAYMENT SECTION ── */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div style={{ background: "var(--surface)", border: "1px solid var(--border-med)", borderRadius: 14, padding: "20px 24px", height: "100%", display: "flex", flexDirection: "column" }}>
            <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 14 }}>Make a Payment</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {["Maya QR", "GCash", "Visa", "Mastercard", "Bank Transfer"].map(m => (
                <span key={m} className="badge badge-blue" style={{ fontSize: "0.75rem", padding: "4px 10px" }}>{m}</span>
              ))}
            </div>
            <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", lineHeight: 1.6, flex: 1 }}>
              Ready to pay an invoice or start a new project? Use our secure Maya payment portal. You can pay via QR, e-wallet, or any major credit/debit card.
            </p>
            <div style={{ marginTop: 24 }}>
              <a 
                href="https://paymaya.me/aimbleitsoluti" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-primary"
                style={{ width: "100%", justifyContent: "center", padding: "12px" }}
              >
                Pay via Maya Portal →
              </a>
              <p style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--text-dim)", marginTop: 12 }}>
                Please include your invoice number in the payment notes.
              </p>
            </div>
          </div>
        </motion.div>

      </div>

      {/* ── 3. PAYMENT HISTORY ── */}
      <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 16 }}>Payment History</h2>
      {loading ? (
        <div style={{ textAlign: "center", padding: 48 }}><div className="spinner" style={{ margin: "0 auto" }} /></div>
      ) : payments.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 24px", background: "var(--surface)", borderRadius: 16, border: "1px dashed var(--border-med)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>💳</div>
          <h3 style={{ fontWeight: 600, marginBottom: 6 }}>No payments yet</h3>
          <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>Your payment records will appear here after your first purchase.</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: "600px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 12, padding: "8px 16px", fontSize: "0.75rem", fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              <span>Description</span><span>Date</span><span>Amount</span><span>Method</span><span>Status</span>
            </div>
            {payments.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 12, padding: "14px 16px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, alignItems: "center", marginBottom: 8, fontSize: "0.875rem" }}>
                  <span style={{ fontWeight: 500 }}>{p.description || p.packageName || "Payment"}</span>
                  <span style={{ color: "var(--text-muted)" }}>{p.createdAt?.toDate?.()?.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }) || "—"}</span>
                  <span style={{ fontWeight: 600 }}>₱{(p.amount || 0).toLocaleString()}</span>
                  <span style={{ color: "var(--text-muted)" }}>{p.method || "—"}</span>
                  <span className={`badge ${statusStyle[p.status] || "badge-blue"}`}>{p.status || "paid"}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}